import React, { useEffect, useState } from 'react';
import { LogOut, Trash2, Mail, Briefcase, FileText, Shield } from 'lucide-react';
import { Container, EyebrowLabel, SectionTitle } from '../components/Section';
import { getAdminToken, setAdminToken, clearAdminToken, verifyAdmin, adminListEnquiries, adminListApplications, adminDeleteCaseStudy, adminDeleteJob, listCaseStudies, listJobs } from '../lib/api';
import { useToast } from '../hooks/use-toast';

export default function Admin() {
  const { toast } = useToast();
  const [authed, setAuthed] = useState(false);
  const [tokenInput, setTokenInput] = useState('');
  const [tab, setTab] = useState('enquiries');
  const [data, setData] = useState({ enquiries: [], applications: [], caseStudies: [], jobs: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    const t = getAdminToken();
    if (t) {
      verifyAdmin().then(() => setAuthed(true)).catch(() => clearAdminToken());
    }
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [enquiries, applications, caseStudies, jobs] = await Promise.all([
        adminListEnquiries(),
        adminListApplications(),
        listCaseStudies(),
        listJobs(),
      ]);
      setData({ enquiries, applications, caseStudies, jobs });
    } catch (e) {
      toast({ title: 'Failed to load', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (authed) loadAll(); }, [authed]);

  const onLogin = async (e) => {
    e.preventDefault();
    setAdminToken(tokenInput.trim());
    try {
      await verifyAdmin();
      setAuthed(true);
      toast({ title: 'Admin session started' });
    } catch {
      clearAdminToken();
      toast({ title: 'Invalid token', variant: 'destructive' });
    }
  };

  const onLogout = () => { clearAdminToken(); setAuthed(false); setTokenInput(''); };

  if (!authed) {
    return (
      <div className="bg-[#FAF7F5] min-h-screen pt-[136px] lg:pt-[168px] pb-24">
        <Container>
          <div className="max-w-md mx-auto bg-white border border-[#EADDE2] rounded-3xl p-8">
            <div className="w-12 h-12 rounded-full bg-[#F2E3E8] flex items-center justify-center text-[#7E2E4A] mb-4"><Shield size={20} /></div>
            <SectionTitle className="text-[26px]">Admin access</SectionTitle>
            <p className="mt-2 text-[13.5px] text-[#4E434A]">Enter your shared admin token to view enquiries, applications and manage jobs & case studies.</p>
            <form onSubmit={onLogin} className="mt-6 space-y-3">
              <input type="password" value={tokenInput} onChange={(e) => setTokenInput(e.target.value)} placeholder="Admin token" className="w-full px-4 py-3 rounded-xl bg-[#FAF7F5] border border-[#EADDE2] focus:border-[#C4506F] outline-none text-[14px]" />
              <button className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1420] text-white text-[14px] px-4 py-3 rounded-full hover:bg-[#3D1A2A]">Sign in</button>
            </form>
          </div>
        </Container>
      </div>
    );
  }

  const tabs = [
    { id: 'enquiries', label: 'Enquiries', icon: Mail, count: data.enquiries.length },
    { id: 'applications', label: 'Applications', icon: FileText, count: data.applications.length },
    { id: 'case-studies', label: 'Case Studies', icon: Shield, count: data.caseStudies.length },
    { id: 'jobs', label: 'Jobs', icon: Briefcase, count: data.jobs.length },
  ];

  return (
    <div className="bg-[#FAF7F5] min-h-screen pt-[136px] lg:pt-[168px] pb-24">
      <Container>
        <div className="flex items-center justify-between">
          <div>
            <EyebrowLabel>Admin</EyebrowLabel>
            <SectionTitle className="mt-3 text-[36px] lg:text-[46px]">Erbity operations</SectionTitle>
          </div>
          <button onClick={onLogout} className="inline-flex items-center gap-1.5 text-[13.5px] text-[#4E434A] hover:text-[#7E2E4A]"><LogOut size={14} /> Sign out</button>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[13px] border transition-colors ${tab === t.id ? 'bg-[#1A1420] text-white border-[#1A1420]' : 'bg-white text-[#4E434A] border-[#EADDE2] hover:border-[#C4506F]'}`}>
              <t.icon size={13} /> {t.label} <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${tab === t.id ? 'bg-white/15' : 'bg-[#F2E3E8] text-[#7E2E4A]'}`}>{t.count}</span>
            </button>
          ))}
          <button onClick={loadAll} className="ml-auto text-[13px] text-[#7E2E4A] border-b border-[#7E2E4A]/30 pb-0.5">Refresh</button>
        </div>

        <div className="mt-6 bg-white border border-[#EADDE2] rounded-3xl overflow-hidden">
          {loading && <div className="p-6 text-[13px] text-[#7A6A72]">Loading…</div>}
          {tab === 'enquiries' && <EnquiryTable rows={data.enquiries} />}
          {tab === 'applications' && <ApplicationTable rows={data.applications} />}
          {tab === 'case-studies' && <CaseStudiesTable rows={data.caseStudies} onDelete={async (id) => { await adminDeleteCaseStudy(id); toast({ title: 'Deleted' }); loadAll(); }} />}
          {tab === 'jobs' && <JobsTable rows={data.jobs} onDelete={async (id) => { await adminDeleteJob(id); toast({ title: 'Deleted' }); loadAll(); }} />}
        </div>
      </Container>
    </div>
  );
}

function Empty({ children }) { return <div className="p-8 text-[13.5px] text-[#7A6A72]">{children}</div>; }
function Cell({ children, className = '' }) { return <td className={`px-4 py-3 text-[13px] text-[#1A1420] ${className}`}>{children}</td>; }
function Head({ children }) { return <th className="px-4 py-3 text-left text-[11px] uppercase tracking-[0.18em] text-[#7A6A72] font-medium bg-[#FAF7F5]">{children}</th>; }

function EnquiryTable({ rows }) {
  if (!rows.length) return <Empty>No enquiries yet.</Empty>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead><tr><Head>When</Head><Head>Name</Head><Head>Company</Head><Head>Service</Head><Head>Industry</Head><Head>Message</Head></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-[#EADDE2]">
              <Cell className="whitespace-nowrap text-[#7A6A72]">{new Date(r.created_at).toLocaleString()}</Cell>
              <Cell><div className="font-medium">{r.name}</div><div className="text-[12px] text-[#7A6A72]">{r.email}</div></Cell>
              <Cell>{r.company}</Cell><Cell>{r.service}</Cell><Cell>{r.industry}</Cell>
              <Cell className="max-w-md"><div className="line-clamp-3 text-[12.5px] text-[#4E434A]">{r.message}</div></Cell>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
function ApplicationTable({ rows }) {
  if (!rows.length) return <Empty>No applications yet.</Empty>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead><tr><Head>When</Head><Head>Candidate</Head><Head>Role</Head><Head>Location</Head><Head>Resume</Head></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-[#EADDE2]">
              <Cell className="whitespace-nowrap text-[#7A6A72]">{new Date(r.created_at).toLocaleString()}</Cell>
              <Cell><div className="font-medium">{r.name}</div><div className="text-[12px] text-[#7A6A72]">{r.email}</div></Cell>
              <Cell>{r.job_title}</Cell><Cell>{r.location || '—'}</Cell>
              <Cell><a href={r.resume_url} target="_blank" rel="noreferrer" className="text-[#7E2E4A] underline">Open</a></Cell>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
function CaseStudiesTable({ rows, onDelete }) {
  if (!rows.length) return <Empty>No case studies yet.</Empty>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead><tr><Head>Title</Head><Head>Industry</Head><Head>Engagement</Head><Head>Time-to-fill</Head><Head></Head></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-[#EADDE2]">
              <Cell><div className="font-medium">{r.title}</div></Cell>
              <Cell>{r.industry}</Cell><Cell>{r.engagement}</Cell><Cell>{r.time_to_fill}</Cell>
              <Cell className="text-right"><button onClick={() => onDelete(r.id)} className="inline-flex items-center gap-1 text-[12.5px] text-[#7E2E4A] hover:text-[#3D1A2A]"><Trash2 size={13} /> Delete</button></Cell>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
function JobsTable({ rows, onDelete }) {
  if (!rows.length) return <Empty>No jobs yet.</Empty>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead><tr><Head>Title</Head><Head>Industry</Head><Head>Location</Head><Head>Type</Head><Head></Head></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-[#EADDE2]">
              <Cell><div className="font-medium">{r.title}</div></Cell>
              <Cell>{r.industry}</Cell><Cell>{r.location}</Cell><Cell>{r.type}</Cell>
              <Cell className="text-right"><button onClick={() => onDelete(r.id)} className="inline-flex items-center gap-1 text-[12.5px] text-[#7E2E4A] hover:text-[#3D1A2A]"><Trash2 size={13} /> Delete</button></Cell>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
